// src/App.js
import React, { Suspense, useEffect } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import PrivateRoute from './utils/PrivateRoute';
import LoadingSpinner from './components/Common/LoadingSpinner/LoadingSpinner';
import MainLayout from './Layout/MainLayout';
import { PATHNAME } from './pathname';
import OperatingPerformanceAdmin from './pages/UnitRegionAdmin/OperatingPerformanceAdmin';
import ProjectDetailsAdmin from './pages/UnitRegionAdmin/ProjectDetailsAdmin';
import './App.css';
import UnitRegionAdminPreview from './pages/UnitRegionAdmin/UnitRegionAdminPreview';
import ReportsPage from './pages/ReportsPage/ReportsPage';
import UnitLevelReportPage from './pages/ReportsPage/UnitLevelReportPage';
import ProjectLevelReportPage from './pages/ReportsPage/ProjectLevelReportPage';
import PmgProjectsPage from './pages/PMG/PmgProjectsPage';
import useAuthBroadcast from './hooks/useAuthBroadcast';
import AdminDirectOrders from './pages/Projects/AdminDirectOrders/AdminDirectOrders';
import AdminAnticipatedOrders from './pages/Projects/AdminAnticipatedOrders/AdminAnticipatedOrders';
import ProjectCustomerGroupWiseReport from './pages/Projects/ProjectCustomerGroupWiseReport/ProjectCustomerGroupWiseReport';
import AdminOtherProjects from './pages/Projects/AdminOtherProjects/AdminOtherProjects';
import AdminOtherProjectsDetails from './pages/Projects/AdminOtherProjects/AdminOtherProjectsDetails';
import InternalOrderMaster from './pages/InternalOrderMaster/InternalOrderMaster';
import IOMasterUploadFiles from './pages/IOMasterUploadFiles/IOMasterUploadFiles';
import IOindasMappingUploadFiles from './pages/IOindasMappingUploadFiles/IOindasMappingUploadFiles';
import IoIndasDataMapping from './pages/IoIndasDataMapping/IoIndasDataMapping';
import IoUnitMapping from './pages/IoUnitMapping/IoUnitMapping';
import IoUnitMappingUploadFiles from './pages/IoUnitMappingUploadFiles/IoUnitMappingUploadFiles';
import IoAllocation from './pages/IoAllocation/IoAllocation';
import { UploadFiles } from './pages/PMG/UploadFiles';
import IoAllocationUploadFiles from './pages/IoAllocationUploadFiles/IoAllocationUploadFiles';
import ExecutionPlan from './pages/ConsolidatedReports/ExecutionPlanReport/index.jsx';
import ExecutionPlanNew from './pages/ConsolidatedReports/ExecutionPlanReportNew/index.jsx';
import BudgetReport from './pages/ConsolidatedReports/BudgetReport/index.jsx';
import OperatingPerformanceReport from './pages/ConsolidatedReports/OperatingPerformanceReport/index.jsx';
import OperatingPerformanceUnitWiseReport from './pages/ConsolidatedReports/OperatingPerformanceUnitWiseReport/index.jsx';
import OperatingPerformanceQuarterWiseReport from './pages/ConsolidatedReports/OperatingPerformanceQuarterWiseReport/index.jsx';
import ActualBudgetReport from './pages/ConsolidatedReports/ActualBudgetReport/index.jsx';
import OtherProjectStatusTable from './pages/Projects/AdminOtherProjects/OtherProjectStatusTable.jsx';
import OtherProjectsDate from './pages/OtherProjectsDate/OtherProjectsDate';
import Masters from './pages/Masters/Masters';
// import ProjectWiseReport from './pages/ConsolidatedReports/ProjectWiseReport/ProjectWiseReport';
import OtherProjectsExecutionPlan from './pages/ConsolidatedReports/OtherProjectsExecutionPlan/index.jsx';
import BusinessSectorWiseExecutionPlan from './pages/ConsolidatedReports/BusinessSectorWiseExecutionPlan/index.jsx';
import BusinessSectorOtherProjects from './pages/ConsolidatedReports/BusinessSectorOtherProjects/index.jsx';
import ProjectUnitWisePlanning from './pages/ConsolidatedReports/ProjectUnitWisePlanning/index.jsx';
import UpdateRevNo from "./pages/UpdateRevNo/UpdateRevNo";
import DeleteRevNo from "./pages/DeleteRevNo/DeleteRevNo";
import DMProjectWiseReport from "./pages/DMAnalytics/DMProjectWiseReport.jsx"
import DMUnitWiseReport from "./pages/DMAnalytics/DMUnitWiseReport.jsx"
import DMReport from "./pages/DMAnalytics/DMReport.jsx"
import DMCharts from "./pages/DMAnalytics/DMCharts.jsx"
import DMCriticalProductsReport from "./pages/DMAnalytics/DMCriticalProductsReport.jsx"
import DMMWProjectWiseReport from "./pages/DMAnalytics/DMMWProjectWiseReport.jsx"
import MasterUploads from "./pages/MasterUploads/MasterUploads";
// Lazy-loaded pages
const LoginPage = React.lazy(() => import('./pages/LoginPage/LoginPage'));
const AdminPage = React.lazy(() => import('./pages/AdminPage/AdminPage'));
const UnauthorizedPage = React.lazy(() => import('./pages/UnauthorizedPage/UnauthorizedPage'));
const NotFoundPage = React.lazy(() => import('./pages/NotFoundPage/NotFoundPage'));
const ServerErrorPage = React.lazy(() => import('./pages/ServerErrorPage/ServerErrorPage'));
const ComponentsPage = React.lazy(() => import('./pages/ComponentsPage/ComponentsPage'));
const ProjectCost = React.lazy(() => import('./pages/BudgetSubmission/ProjectCost'));
const PackagesListing = React.lazy(() => import('./pages/PackagesListing/PackagesListing'));
const Dashboard = React.lazy(() => import('./pages/Dashboard/Dashboard'));
const ProductsListing = React.lazy(() => import('./pages/ProductsListing/ProductsListing'));
const OtherProjects = React.lazy(() => import('./pages/BudgetSubmission/OtherProjects/OtherProjects'));
const PerformancePlanning = React.lazy(() => import('./pages/BudgetSubmission/PerformancePlanning'));
const GuidelinesPage = React.lazy(() => import('./pages/GuidelinesPage/GuidelinesPage'));
const DirectOrders = React.lazy(() => import('./pages/BudgetSubmission/DirectOrders/DirectOrders'));
const DirectOrdersSummary = React.lazy(() => import('./pages/BudgetSubmission/DirectOrders/DirectOrdersSummary'));
const DirectOrdersUpload = React.lazy(() => import('./pages/BudgetSubmission/DirectOrders/DirectOrdersUpload'));
const DirectOrdersDetails = React.lazy(() => import('./pages/BudgetSubmission/DirectOrders/DirectOrdersDetails'));
const IUT = React.lazy(() => import('./pages/BudgetSubmission/IUT/IUT'));
const IUTSummary = React.lazy(() => import('./pages/BudgetSubmission/IUT/IUTSummary'));
const IUTUpload = React.lazy(() => import('./pages/BudgetSubmission/IUT/IUTUpload'));
const IUTDetails = React.lazy(() => import('./pages/BudgetSubmission/IUT/IUTDetails'));
const OperatingPerformance = React.lazy(() => import('./pages/OperatingPerformance/OperatingPerformance'));
const OPUploadFiles = React.lazy(() => import('./pages/OperatingPerformance/OPUploadFiles'));
const AnticipatedOrders = React.lazy(() => import('./pages/BudgetSubmission/AnticipatedOrders/AnticipatedOrders'));
const AOSummeryPage = React.lazy(() => import('./pages/BudgetSubmission/AnticipatedOrders/AOSummeryPage'));
const AOUploadFilesPage = React.lazy(() => import('./pages/BudgetSubmission/AnticipatedOrders/AOUploadFilesPage'));
const AODetailsPage = React.lazy(() => import('./pages/BudgetSubmission/AnticipatedOrders/AODetailsPage'));
const ChangePasswordPage = React.lazy(() => import('./pages/ChangePasswordPage/ChangePasswordPage'));
const UnitRegionAdmin = React.lazy(() => import('./pages/UnitRegionAdmin/UnitRegionAdmin'));
const UnitRegionDetail = React.lazy(() => import('./pages/UnitRegionAdmin/UnitRegionDetail'));

const IndAsProjectInfo = React.lazy(() => import('./pages/BudgetSubmission/OtherProjects/IndAsProjectInfo'));
const OtherProjectUploadFiles = React.lazy(() => import('./pages/BudgetSubmission/OtherProjects/OtherProjectsUploadFiles'));
const IndProjectInfo = React.lazy(() => import('./pages/BudgetSubmission/OtherProjects/IndProjectInfo/IndProjectInfo'));
const ActiveProjectDetails = React.lazy(() => import('./pages/Projects/ActiveProjects/ActiveProjectDetails/ActiveProjectDetails'));
const UnitDetails = React.lazy(() => import('./pages/Projects/ActiveProjects/UnitDetails/UnitDetails'));
const ActiveProjects = React.lazy(() => import('./pages/Projects/ActiveProjects/ActiveProjects'));
const DataSubmissionStatus = React.lazy(() => import('./pages/Projects/ActiveProjects/DataSubmissionStatus/DataSubmissionStatus'));
const AllProjects = React.lazy(() => import('./pages/Projects/AllProjects/AllProjects'));
const ConsolidatedProjectWiseReport = React.lazy(() => import('./pages/ConsolidatedReports/ProjectWiseReport/ProjectWiseReport'));
const ConsolidatedUnitWiseReport = React.lazy(() => import('./pages/ConsolidatedReports/UnitWiseReport/UnitWiseReport.jsx'));
const ConsolidatedIUTReport = React.lazy(() => import('./pages/ConsolidatedReports/IUTReport/IUTReport.jsx'));
const ConsolidatedIUTNonBhelReport = React.lazy(() => import('./pages/ConsolidatedReports/IUTNonBhel/IUTNonBhel.jsx'));
const ConsolidatedYearlyCharts = React.lazy(() => import('./pages/ConsolidatedReports/YearlyCharts/YearlyCharts'));
const ConsolidatedQuarterlyCharts = React.lazy(() => import('./pages/ConsolidatedReports/QuarterlyCharts/QuarterlyCharts'));
const BalanceRevenue = React.lazy(() => import('./pages/BalanceRevenue/index'));
const NonIndasProjects = React.lazy(() => import('./pages/NonIndasProjects/NonIndasProjects'));

const App = () => {
        const location = useLocation();
        //  useAuthBroadcast();

        return (
                <Suspense fallback={<LoadingSpinner />}>
                        <Routes key={location.pathname}>
                                <Route
                                        path="/"
                                        element={
                                                <Navigate
                                                        to="/performance"
                                                        replace
                                                />
                                        }
                                />
                                <Route
                                        path="/login"
                                        element={<LoginPage />}
                                />
                                <Route
                                        path="/change-password"
                                        element={<ChangePasswordPage />}
                                />
                                <Route
                                        path="/unauthorized"
                                        element={<UnauthorizedPage />}
                                />
                                <Route element={<MainLayout />}>
                                        {/* HomePage as parent route */}
                                        <Route
                                                path="components"
                                                element={<ComponentsPage />}
                                        />
                                        {/* Private Routes */}

                                        <Route
                                                path="/performance"
                                                element={
                                                        <PrivateRoute roles={['region', 'admin', 'unit', 'super_admin']}>
                                                                <PerformancePlanning />{' '}
                                                        </PrivateRoute>
                                                }></Route>

                                        <Route
                                                path="/dashboard"
                                                element={
                                                        <PrivateRoute roles={['admin', 'super_admin']}>
                                                                <Dashboard />{' '}
                                                        </PrivateRoute>
                                                }></Route>
                                        <Route
                                                path="/performance/project-cost/:ioNumber"
                                                element={
                                                        <PrivateRoute roles={['unit', 'admin', 'region', 'super_admin']}>
                                                                <ProjectCost />
                                                        </PrivateRoute>
                                                }
                                        />

                                        <Route
                                                path="/performance/other-projects"
                                                element={
                                                        <PrivateRoute roles={['unit', 'admin', 'region', 'super_admin']}>
                                                                <IndAsProjectInfo />
                                                        </PrivateRoute>
                                                }
                                        />
                                        <Route
                                                path="/performance/other-projects/summary"
                                                element={
                                                        <PrivateRoute roles={['unit', 'admin', 'region', 'super_admin']}>
                                                                <OtherProjects />
                                                        </PrivateRoute>
                                                }
                                        />
                                        <Route
                                                path="/performance/other-projects/summary/upload-files"
                                                element={
                                                        <PrivateRoute roles={['unit', 'admin', 'region', 'super_admin']}>
                                                                <OtherProjectUploadFiles />
                                                        </PrivateRoute>
                                                }
                                        />
                                        <Route
                                                path="/performance/other-projects/summary/upload-files/final-preview"
                                                element={
                                                        <PrivateRoute roles={['unit', 'admin', 'region', 'super_admin']}>
                                                                <OtherProjects />
                                                        </PrivateRoute>
                                                }
                                        />
                                        <Route
                                                path="/performance/other-projects/summary/upload-files/final-preview/final-preview-other-projects"
                                                element={
                                                        <PrivateRoute roles={['unit', 'admin', 'region', 'super_admin']}>
                                                                <IndProjectInfo />
                                                        </PrivateRoute>
                                                }
                                        />
                                        <Route
                                                path={PATHNAME.ADMIN_OTHER_PROJECTS_DATE}
                                                element={
                                                        <PrivateRoute roles={['admin', 'super_admin']}>
                                                                <OtherProjectsDate />
                                                        </PrivateRoute>
                                                }
                                        />
                                        <Route
                                                path="/performance/direct-orders"
                                                element={
                                                        <PrivateRoute roles={['unit', 'admin', 'region', 'super_admin']}>
                                                                <DirectOrders />
                                                        </PrivateRoute>
                                                }
                                        />
                                        <Route
                                                path="/performance/anticipated-orders"
                                                element={
                                                        <PrivateRoute roles={['unit', 'region']}>
                                                                <AnticipatedOrders />
                                                        </PrivateRoute>
                                                }
                                        />
                                        <Route
                                                path="/performance/anticipated-orders/list"
                                                element={
                                                        <PrivateRoute roles={['unit', 'region']}>
                                                                <AnticipatedOrders />
                                                        </PrivateRoute>
                                                }
                                        />
                                        <Route
                                                path="/performance/anticipated-orders/list/summary"
                                                element={
                                                        <PrivateRoute roles={['unit', 'region']}>
                                                                <AOSummeryPage />
                                                        </PrivateRoute>
                                                }
                                        />
                                        <Route
                                                path="/performance/anticipated-orders/list/summary/upload-files"
                                                element={
                                                        <PrivateRoute roles={['unit', 'region']}>
                                                                <AOUploadFilesPage />
                                                        </PrivateRoute>
                                                }
                                        />
                                        <Route
                                                path="/performance/anticipated-orders/list/summary/upload-files/final-preview"
                                                element={
                                                        <PrivateRoute roles={['unit', 'region']}>
                                                                <AOSummeryPage />
                                                        </PrivateRoute>
                                                }
                                        />
                                        <Route
                                                path="/performance/anticipated-orders/list/summary/upload-files/final-preview/details"
                                                element={
                                                        <PrivateRoute roles={['unit', 'region']}>
                                                                <AODetailsPage />
                                                        </PrivateRoute>
                                                }
                                        />
                                        <Route
                                                path="/performance/anticipated-orders/list/summary/details"
                                                element={
                                                        <PrivateRoute roles={['unit', 'admin', 'region', 'super_admin']}>
                                                                <AODetailsPage />
                                                        </PrivateRoute>
                                                }
                                        />
                                        <Route
                                                path="/performance/direct-orders/summary"
                                                element={
                                                        <PrivateRoute roles={['unit', 'admin', 'region', 'super_admin']}>
                                                                <DirectOrdersSummary />
                                                        </PrivateRoute>
                                                }
                                        />
                                        <Route
                                                path="/performance/direct-orders/summary/upload"
                                                element={
                                                        <PrivateRoute roles={['unit', 'admin', 'region', 'super_admin']}>
                                                                <DirectOrdersUpload />
                                                        </PrivateRoute>
                                                }
                                        />
                                        <Route
                                                path="/performance/direct-orders/summary/upload/final-preview"
                                                element={
                                                        <PrivateRoute roles={['unit', 'region']}>
                                                                <DirectOrdersSummary />
                                                        </PrivateRoute>
                                                }
                                        />

                                        <Route
                                                path="/performance/direct-orders/summary/upload/final-preview/details"
                                                element={
                                                        <PrivateRoute roles={['unit', 'region']}>
                                                                <DirectOrdersDetails />
                                                        </PrivateRoute>
                                                }
                                        />
                                        <Route
                                                path="/performance/iut"
                                                element={
                                                        <PrivateRoute roles={['unit', 'admin', 'region', 'super_admin']}>
                                                                <IUT />
                                                        </PrivateRoute>
                                                }
                                        />
                                        <Route
                                                path="/performance/iut/summary"
                                                element={
                                                        <PrivateRoute roles={['unit', 'admin', 'region', 'super_admin']}>
                                                                <IUTSummary />
                                                        </PrivateRoute>
                                                }
                                        />
                                        <Route
                                                path="/performance/iut/summary/upload"
                                                element={
                                                        <PrivateRoute roles={['unit', 'admin', 'region', 'super_admin']}>
                                                                <IUTUpload />
                                                        </PrivateRoute>
                                                }
                                        />
                                        <Route
                                                path="/performance/iut/summary/upload/final-preview"
                                                element={
                                                        <PrivateRoute roles={['unit', 'region']}>
                                                                <IUTSummary />
                                                        </PrivateRoute>
                                                }
                                        />
                                        <Route
                                                path="/performance/iut/summary/upload/final-preview/details"
                                                element={
                                                        <PrivateRoute roles={['unit', 'region']}>
                                                                <IUTDetails />
                                                        </PrivateRoute>
                                                }
                                        />
                                        <Route
                                                path="/user"
                                                element={
                                                        <PrivateRoute roles={['user']}>
                                                                <AdminPage />
                                                        </PrivateRoute>
                                                }
                                        />
                                        {/* Dynamic Routes */}
                                        <Route
                                                path="/user/:userId"
                                                element={
                                                        <PrivateRoute roles={['admin', 'user', 'super_admin']}>
                                                                <AdminPage />
                                                        </PrivateRoute>
                                                }
                                        />
                                        <Route
                                                path="/packages"
                                                element={
                                                        <PrivateRoute roles={['admin', 'region', 'unit']}>
                                                                <PackagesListing />
                                                        </PrivateRoute>
                                                }
                                        />
                                        <Route
                                                path={PATHNAME.ADMIN_UPDATE_REV_NO}
                                                element={
                                                        <PrivateRoute roles={["admin", "super_admin"]}>
                                                                <UpdateRevNo />
                                                        </PrivateRoute>
                                                }
                                        />
                                        <Route
                                                path={PATHNAME.ADMIN_DELETE_REV_NO}
                                                element={
                                                        <PrivateRoute roles={["admin", "super_admin"]}>
                                                                <DeleteRevNo />
                                                        </PrivateRoute>
                                                }
                                        />
                                        <Route
                                                path={PATHNAME.ADMIN_MASTER_UPLOADS}
                                                element={
                                                        <PrivateRoute roles={["admin", "super_admin"]}>
                                                                <MasterUploads />
                                                        </PrivateRoute>
                                                }
                                        />

                                        <Route
                                                path={PATHNAME.ADMIN_DM_ANALYTICS_PROJECT_WISE_REPORT}
                                                element={
                                                        <PrivateRoute roles={["admin"]}>
                                                                <DMProjectWiseReport />
                                                        </PrivateRoute>
                                                }
                                        />
                                        <Route
                                                path={PATHNAME.ADMIN_DM_ANALYTICS_UNIT_WISE_REPORT}
                                                element={
                                                        <PrivateRoute roles={["admin"]}>
                                                                <DMUnitWiseReport />
                                                        </PrivateRoute>
                                                }
                                        />
                                        <Route
                                                path={PATHNAME.ADMIN_DM_ANALYTICS_REPORT}
                                                element={
                                                        <PrivateRoute roles={["admin"]}>
                                                                <DMReport />
                                                        </PrivateRoute>
                                                }
                                        />
                                        <Route
                                                path={PATHNAME.ADMIN_DM_ANALYTICS_CHARTS}
                                                element={
                                                        <PrivateRoute roles={["admin"]}>
                                                                <DMCharts />
                                                        </PrivateRoute>
                                                }
                                        />
                                        <Route
                                                path={PATHNAME.ADMIN_DM_ANALYTICS_CRITICAL_PRODUCTS_REPORT}
                                                element={
                                                        <PrivateRoute roles={["admin"]}>
                                                                <DMCriticalProductsReport />
                                                        </PrivateRoute>
                                                }
                                        />
                                        <Route
                                                path={PATHNAME.ADMIN_DM_ANALYTICS_MW_PROJECT_WISE_REPORT}
                                                element={
                                                        <PrivateRoute roles={["admin"]}>
                                                                <DMMWProjectWiseReport />
                                                        </PrivateRoute>
                                                }
                                        />

                                        <Route
                                                path="/pmg-projects"
                                                element={
                                                        <PrivateRoute roles={['admin']}>
                                                                <PmgProjectsPage />
                                                        </PrivateRoute>
                                                }
                                        />
                                        <Route
                                                path="/pmg-projects/upload-files"
                                                element={
                                                        <PrivateRoute roles={['admin']}>
                                                                <UploadFiles />
                                                        </PrivateRoute>
                                                }
                                        />
                                        <Route
                                                path="/masters"
                                                element={
                                                        <PrivateRoute roles={['admin']}>
                                                                <Masters />
                                                        </PrivateRoute>
                                                }
                                        />

                                        <Route
                                                path="/internal-order-master"
                                                element={
                                                        <PrivateRoute roles={['admin']}>
                                                                <InternalOrderMaster />
                                                        </PrivateRoute>
                                                }
                                        />

                                        <Route
                                                path="/internal-order-master/upload-csv"
                                                element={
                                                        <PrivateRoute roles={['admin']}>
                                                                <IOMasterUploadFiles />
                                                        </PrivateRoute>
                                                }
                                        />

                                        <Route
                                                path="/io-indas-data-mapping"
                                                element={
                                                        <PrivateRoute roles={['admin']}>
                                                                <IoIndasDataMapping />
                                                        </PrivateRoute>
                                                }
                                        />

                                        <Route
                                                path="io-indas-data-mapping/upload-csv"
                                                element={
                                                        <PrivateRoute roles={['admin']}>
                                                                <IOindasMappingUploadFiles />
                                                        </PrivateRoute>
                                                }
                                        />

                                        <Route
                                                path="/io-unit-mapping"
                                                element={
                                                        <PrivateRoute roles={['admin']}>
                                                                <IoUnitMapping />
                                                        </PrivateRoute>
                                                }
                                        />
                                        {/* <Route
                                                path="/order-outstanding"
                                                element={
                                                        <PrivateRoute roles={['admin']}>
                                                                <BalanceRevenue />
                                                        </PrivateRoute>
                                                }
                                        /> */}
                                        {/* <Route
                                                path="/execution-plan"
                                                element={
                                                        <PrivateRoute roles={['admin']}>
                                                                <ExecutionPlan />
                                                        </PrivateRoute>
                                                }
                                        />
                                        <Route
                                                path="/budget-report"
                                                element={
                                                        <PrivateRoute roles={['admin']}>
                                                                <BudgetReport />
                                                        </PrivateRoute>
                                                }
                                        /> */}

                                        <Route
                                                path="/io-unit-mapping/upload-csv"
                                                element={
                                                        <PrivateRoute roles={['admin']}>
                                                                <IoUnitMappingUploadFiles />
                                                        </PrivateRoute>
                                                }
                                        />

                                        <Route
                                                path="/io-allocation"
                                                element={
                                                        <PrivateRoute roles={['admin']}>
                                                                <IoAllocation />
                                                        </PrivateRoute>
                                                }
                                        />

                                        <Route
                                                path="/io-allocation/upload-csv"
                                                element={
                                                        <PrivateRoute roles={['admin']}>
                                                                <IoAllocationUploadFiles />
                                                        </PrivateRoute>
                                                }
                                        />

                                        <Route
                                                path="/reports"
                                                element={
                                                        <PrivateRoute roles={['admin']}>
                                                                <ReportsPage />
                                                        </PrivateRoute>
                                                }
                                        />
                                        <Route
                                                path="/reports/unit-level"
                                                element={
                                                        <PrivateRoute roles={['admin']}>
                                                                <UnitLevelReportPage />
                                                        </PrivateRoute>
                                                }
                                        />
                                        <Route
                                                path="/reports/project-level"
                                                element={
                                                        <PrivateRoute roles={['admin']}>
                                                                <ProjectLevelReportPage />
                                                        </PrivateRoute>
                                                }
                                        />
                                        <Route
                                                path="/products"
                                                element={
                                                        <PrivateRoute roles={['admin', 'region', 'unit']}>
                                                                <ProductsListing />
                                                        </PrivateRoute>
                                                }
                                        />
                                        <Route
                                                path="/guidelines"
                                                element={
                                                        <PrivateRoute roles={['admin', 'unit', 'region']}>
                                                                <GuidelinesPage />
                                                        </PrivateRoute>
                                                }
                                        />

                                        <Route
                                                path="/operating-performance"
                                                element={
                                                        <PrivateRoute roles={['region', 'unit']}>
                                                                <OperatingPerformance />
                                                        </PrivateRoute>
                                                }
                                        />

                                        <Route
                                                path="/operating-performance/upload-files"
                                                element={
                                                        <PrivateRoute roles={['region', 'unit']}>
                                                                <OPUploadFiles />
                                                        </PrivateRoute>
                                                }
                                        />

                                        <Route
                                                path="/operating-performance/upload-files/final-preview"
                                                element={
                                                        <PrivateRoute roles={['region', 'unit']}>
                                                                <OperatingPerformance />
                                                        </PrivateRoute>
                                                }
                                        />
                                        <Route
                                                path="/non-indas-projects"
                                                element={
                                                        <PrivateRoute roles={['unit', 'region']}>
                                                                <NonIndasProjects />
                                                        </PrivateRoute>
                                                }
                                        />
                                        <Route
                                                path={PATHNAME.ADMIN_UNIT_REGION}
                                                element={
                                                        <PrivateRoute roles={['admin', 'super_admin']}>
                                                                <UnitRegionAdmin />
                                                        </PrivateRoute>
                                                }
                                        />
                                        <Route
                                                path="/unit-region/:unitRegionName/final-preview"
                                                element={
                                                        <PrivateRoute roles={['admin', 'super_admin']}>
                                                                <DirectOrdersSummary />
                                                        </PrivateRoute>
                                                }
                                        />
                                        <Route
                                                path="/unit-region/:unitRegionName/final-preview/details"
                                                element={
                                                        <PrivateRoute roles={['admin', 'super_admin']}>
                                                                <DirectOrdersDetails />
                                                        </PrivateRoute>
                                                }
                                        />
                                        <Route
                                                path="/projects-direct-orders/:unitRegionName/final-preview"
                                                element={
                                                        <PrivateRoute roles={['admin', 'super_admin']}>
                                                                <DirectOrdersSummary />
                                                        </PrivateRoute>
                                                }
                                        />
                                        <Route
                                                path="/projects-direct-orders/:unitRegionName"
                                                element={
                                                        <PrivateRoute roles={['admin', 'super_admin']}>
                                                                <DirectOrdersSummary />
                                                        </PrivateRoute>
                                                }
                                        />
                                        <Route
                                                path="/projects-direct-orders/:unitRegionName/final-preview/details"
                                                element={
                                                        <PrivateRoute roles={['admin', 'super_admin']}>
                                                                <DirectOrdersDetails />
                                                        </PrivateRoute>
                                                }
                                        />
                                        <Route
                                                path="/unit-region/:unitRegionName/final-preview-anticipated-order"
                                                element={
                                                        <PrivateRoute roles={['admin', 'super_admin']}>
                                                                <AOSummeryPage />
                                                        </PrivateRoute>
                                                }
                                        />
                                        <Route
                                                path="/unit-region/:unitRegionName/final-preview-anticipated-order/details"
                                                element={
                                                        <PrivateRoute roles={['admin', 'super_admin']}>
                                                                <AODetailsPage />
                                                        </PrivateRoute>
                                                }
                                        />
                                        <Route
                                                path="/projects-anticipated-orders/:unitRegionName/final-preview-anticipated-order"
                                                element={
                                                        <PrivateRoute roles={['admin', 'super_admin']}>
                                                                <AOSummeryPage />
                                                        </PrivateRoute>
                                                }
                                        />
                                        <Route
                                                path="/projects-anticipated-orders/:unitRegionName"
                                                element={
                                                        <PrivateRoute roles={['admin', 'super_admin']}>
                                                                <AOSummeryPage />
                                                        </PrivateRoute>
                                                }
                                        />
                                        <Route
                                                path="/projects-anticipated-orders/:unitRegionName/final-preview-anticipated-order/details"
                                                element={
                                                        <PrivateRoute roles={['admin', 'super_admin']}>
                                                                <AODetailsPage />
                                                        </PrivateRoute>
                                                }
                                        />

                                        <Route
                                                path="/unit-region/:unitRegionName/final-preview-other-projects"
                                                element={
                                                        <PrivateRoute roles={['admin', 'super_admin']}>
                                                                <OtherProjects />
                                                        </PrivateRoute>
                                                }
                                        />
                                        <Route
                                                path="/unit-region/:unitRegionName/final-preview-other-projects/final-preview-other-projects"
                                                element={
                                                        <PrivateRoute roles={['admin', 'super_admin']}>
                                                                <IndProjectInfo />
                                                        </PrivateRoute>
                                                }
                                        />
                                        <Route
                                                path={`/${PATHNAME.ADMIN_OTHER_PROJECTS}/:unitRegionName/final-preview-other-projects`}
                                                element={
                                                        <PrivateRoute roles={['admin', 'super_admin']}>
                                                                <OtherProjects />
                                                        </PrivateRoute>
                                                }
                                        />
                                        <Route
                                                path={`/${PATHNAME.ADMIN_OTHER_PROJECTS}/:unitRegionName`}
                                                element={
                                                        <PrivateRoute roles={['admin', 'super_admin']}>
                                                                <OtherProjects />
                                                        </PrivateRoute>
                                                }
                                        />

                                        <Route
                                                path={`/${PATHNAME.ADMIN_OTHER_PROJECTS}/:unitRegionName/final-preview-other-projects/final-preview-other-projects`}
                                                element={
                                                        <PrivateRoute roles={['admin', 'super_admin']}>
                                                                <IndProjectInfo />
                                                        </PrivateRoute>
                                                }
                                        />
                                        <Route
                                                path={PATHNAME?.ADMIN_OPERATING}
                                                element={
                                                        <PrivateRoute roles={['admin', 'super_admin']}>
                                                                <OperatingPerformanceAdmin />
                                                        </PrivateRoute>
                                                }
                                        />
                                        <Route
                                                path={PATHNAME?.ADMIN_PROJECT_DETAILS}
                                                element={
                                                        <PrivateRoute roles={['admin', 'super_admin']}>
                                                                <ProjectDetailsAdmin />
                                                        </PrivateRoute>
                                                }
                                        />

                                        <Route
                                                path={PATHNAME.ADMIN_UNIT_REGION_DETAILS}
                                                element={
                                                        <PrivateRoute roles={['admin', 'super_admin']}>
                                                                <UnitRegionDetail />
                                                        </PrivateRoute>
                                                }
                                        />
                                        <Route
                                                path={PATHNAME.ADMIN_UNIT_REGION_FINAL_PREVIEW}
                                                element={
                                                        <PrivateRoute roles={['admin', 'super_admin']}>
                                                                <UnitRegionAdminPreview />
                                                        </PrivateRoute>
                                                }
                                        />
                                        <Route
                                                path={PATHNAME.ADMIN_ACTIVE_PROJECTS}
                                                element={
                                                        <PrivateRoute roles={['admin', 'super_admin']}>
                                                                <ActiveProjects />
                                                        </PrivateRoute>
                                                }
                                        />
                                        <Route
                                                path={PATHNAME.ADMIN_DIRECT_ORDERS}
                                                element={
                                                        <PrivateRoute roles={['admin', 'super_admin']}>
                                                                <AdminDirectOrders />
                                                        </PrivateRoute>
                                                }
                                        />
                                        <Route
                                                path={PATHNAME.ADMIN_ANTICIPATED_ORDERS}
                                                element={
                                                        <PrivateRoute roles={['admin', 'super_admin']}>
                                                                <AdminAnticipatedOrders />
                                                        </PrivateRoute>
                                                }
                                        />
                                        <Route
                                                path={PATHNAME.ADMIN_PROJECT_CUSTOMER_GROUP_WISE}
                                                element={
                                                        <PrivateRoute roles={['admin', 'super_admin']}>
                                                                <ProjectCustomerGroupWiseReport />
                                                        </PrivateRoute>
                                                }
                                        />
                                        <Route
                                                path={PATHNAME.ADMIN_OTHER_PROJECTS}
                                                element={
                                                        <PrivateRoute roles={['admin', 'super_admin']}>
                                                                <AdminOtherProjects />
                                                        </PrivateRoute>
                                                }
                                        />
                                        <Route
                                                path={PATHNAME.ADMIN_OTHER_PROJECTS_DATA_SUBMISSION_STATUS}
                                                element={
                                                        <PrivateRoute roles={['admin', 'super_admin']}>
                                                                <OtherProjectStatusTable />
                                                        </PrivateRoute>
                                                }
                                        />
                                        <Route
                                                path={`/${PATHNAME.ADMIN_OTHER_PROJECTS_DETAILS}`}
                                                element={
                                                        <PrivateRoute roles={['admin', 'super_admin']}>
                                                                <AdminOtherProjects />
                                                        </PrivateRoute>
                                                }
                                        />
                                        <Route
                                                path={`/${PATHNAME.ADMIN_OTHER_PROJECTS_DETAILS}/:projectName`}
                                                element={
                                                        <PrivateRoute roles={['admin', 'super_admin']}>
                                                                <AdminOtherProjectsDetails />
                                                        </PrivateRoute>
                                                }
                                        />
                                        <Route
                                                path={PATHNAME.ADMIN_ACTIVE_PROJECTS_DATA_SUBMISSION_STATUS}
                                                element={
                                                        <PrivateRoute roles={['admin', 'super_admin']}>
                                                                <DataSubmissionStatus />
                                                        </PrivateRoute>
                                                }
                                        />
                                        <Route
                                                path={`${PATHNAME.ADMIN_ACTIVE_PROJECT_DETAILS}/:projectName`}
                                                element={
                                                        <PrivateRoute roles={['admin', 'super_admin']}>
                                                                <ActiveProjectDetails />
                                                        </PrivateRoute>
                                                }
                                        />
                                        <Route
                                                path={PATHNAME.ADMIN_ACTIVE_PROJECT_UNIT_DETAIL}
                                                element={
                                                        <PrivateRoute roles={['admin', 'super_admin']}>
                                                                <UnitDetails />
                                                        </PrivateRoute>
                                                }
                                        />

                                        <Route
                                                path={PATHNAME.ADMIN_ALL_PROJECTS}
                                                element={
                                                        <PrivateRoute roles={['admin', 'super_admin']}>
                                                                <AllProjects />
                                                        </PrivateRoute>
                                                }
                                        />
                                        <Route
                                                path={PATHNAME.ADMIN_CONSOLIDATED_PROJECT_WISE_REPORT}
                                                element={
                                                        <PrivateRoute roles={['admin', 'super_admin']}>
                                                                <ConsolidatedProjectWiseReport />
                                                        </PrivateRoute>
                                                }
                                        />
                                        <Route
                                                path={PATHNAME.ADMIN_CONSOLIDATED_BUDGET_REPORT}
                                                element={
                                                        <PrivateRoute roles={['admin', 'super_admin', "unit", "region"]}>
                                                                <BudgetReport />
                                                        </PrivateRoute>
                                                }
                                        />
                                        <Route
                                                path={PATHNAME.ADMIN_CONSOLIDATED_ACTUAL_BUDGET_REPORT}
                                                element={
                                                        <PrivateRoute roles={['admin', 'super_admin', "unit", "region"]}>
                                                                <ActualBudgetReport />
                                                        </PrivateRoute>
                                                }
                                        />
                                        <Route
                                                path={PATHNAME.ADMIN_CONSOLIDATED_OPERATING_PERFORMANCE_REPORT}
                                                element={
                                                        <PrivateRoute roles={['admin', 'super_admin', "unit", "region"]}>
                                                                <OperatingPerformanceReport />
                                                        </PrivateRoute>
                                                }
                                        />
                                        <Route
                                                path={PATHNAME.ADMIN_CONSOLIDATED_OPERATING_PERFORMANCE_UNITWISE_REPORT}
                                                element={
                                                        <PrivateRoute roles={['admin', 'super_admin', "unit", "region"]}>
                                                                <OperatingPerformanceUnitWiseReport />
                                                        </PrivateRoute>
                                                }
                                        />
                                        <Route
                                                path={PATHNAME.ADMIN_CONSOLIDATED_OPERATING_PERFORMANCE_QUARTERWISE_REPORT}
                                                element={
                                                        <PrivateRoute roles={['admin', 'super_admin', "unit", "region"]}>
                                                                <OperatingPerformanceQuarterWiseReport />
                                                        </PrivateRoute>
                                                }
                                        />
                                        <Route
                                                path={PATHNAME.ADMIN_CONSOLIDATED_EXECUTION_REPORT}
                                                element={
                                                        <PrivateRoute roles={['admin', 'super_admin', 'unit', 'region']}>
                                                                <ExecutionPlan />
                                                        </PrivateRoute>
                                                }
                                        />
                                        <Route
                                                path={PATHNAME.ADMIN_CONSOLIDATED_OTHER_PROJECTS_EXECUTION_PLAN}
                                                element={
                                                        <PrivateRoute roles={['admin', 'super_admin', 'unit', 'region']}>
                                                                <OtherProjectsExecutionPlan />
                                                        </PrivateRoute>
                                                }
                                        />
                                        <Route
                                                path={PATHNAME.ADMIN_CONSOLIDATED_BUSINESS_SECTOR_WISE_EXECUTION_PLAN}
                                                element={
                                                        <PrivateRoute roles={['admin', 'super_admin']}> <BusinessSectorWiseExecutionPlan /> </PrivateRoute>
                                                }
                                        />
                                        <Route
                                                path={
                                                        PATHNAME.ADMIN_CONSOLIDATED_BUSINESS_SECTOR_OTHER_PROJECTS
                                                }
                                                element={<BusinessSectorOtherProjects />
                                                }
                                        />

                                        <Route
                                                path={PATHNAME.ADMIN_CONSOLIDATED_PROJECT_UNIT_WISE_PLANNING}
                                                element={
                                                        <PrivateRoute roles={['admin', 'super_admin']}>
                                                                <ProjectUnitWisePlanning />
                                                        </PrivateRoute>
                                                }
                                        />
                                        <Route
                                                path={PATHNAME.ADMIN_CONSOLIDATED_EXECUTION_REPORT_NEW}
                                                element={
                                                        <PrivateRoute roles={['admin', 'super_admin']}>
                                                                <ExecutionPlanNew />
                                                        </PrivateRoute>
                                                }
                                        />
                                        <Route
                                                path={PATHNAME.ADMIN_CONSOLIDATED_ORDER_OUTSTANDING}
                                                element={
                                                        <PrivateRoute roles={['admin', 'super_admin']}>
                                                                <BalanceRevenue />
                                                        </PrivateRoute>
                                                }
                                        />

                                        <Route
                                                path={PATHNAME.ADMIN_CONSOLIDATED_UNIT_WISE_REPORT}
                                                element={
                                                        <PrivateRoute roles={['admin', 'super_admin']}>
                                                                <ConsolidatedUnitWiseReport />
                                                        </PrivateRoute>
                                                }
                                        />

                                        <Route
                                                path={PATHNAME.ADMIN_CONSOLIDATED_IUT_REPORT}
                                                element={
                                                        <PrivateRoute roles={['admin', 'super_admin']}>
                                                                <ConsolidatedIUTReport />
                                                        </PrivateRoute>
                                                }
                                        />

                                        <Route
                                                path={PATHNAME.ADMIN_CONSOLIDATED_IUT_NON_BHEL_REPORT}
                                                element={
                                                        <PrivateRoute roles={['admin', 'super_admin']}>
                                                                <ConsolidatedIUTNonBhelReport />
                                                        </PrivateRoute>
                                                }
                                        />

                                        <Route
                                                path={PATHNAME.ADMIN_CONSOLIDATED_YEARLY_CHART}
                                                element={
                                                        <PrivateRoute roles={['admin', 'super_admin']}>
                                                                <ConsolidatedYearlyCharts />
                                                        </PrivateRoute>
                                                }
                                        />

                                        <Route
                                                path={PATHNAME.ADMIN_CONSOLIDATED_QUARTERLY_CHART}
                                                element={
                                                        <PrivateRoute roles={['admin', 'super_admin']}>
                                                                <ConsolidatedQuarterlyCharts />
                                                        </PrivateRoute>
                                                }
                                        />

                                </Route>

                                {/* 404 Page Not Found */}
                                <Route
                                        path="*"
                                        element={<NotFoundPage />}
                                />
                                <Route
                                        path="/404"
                                        element={<NotFoundPage />}
                                />
                                <Route
                                        path="/error"
                                        element={<ServerErrorPage />}
                                />
                        </Routes>
                </Suspense>
        );
};

export default App;
